import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui';
import { usePasskey, getDeviceLabel } from './usePasskey';
import { FaceSmileIcon, FingerPrintIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

interface PasskeyPromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Modal to promote passkey registration after login
 * Shows device-specific messaging (Face ID, Touch ID, Windows Hello)
 */
export function PasskeyPromotionModal({
  isOpen,
  onClose,
  onSuccess,
}: PasskeyPromotionModalProps) {
  const { t } = useTranslation();
  const { register, isLoading, error, dismissPromotion } = usePasskey();

  const deviceLabel = getDeviceLabel();
  
  // Determine which icon to show based on device
  const DeviceIcon = deviceLabel.includes('Face') 
    ? FaceSmileIcon 
    : deviceLabel.includes('Touch') || deviceLabel.includes('huella') || deviceLabel.includes('Biometría')
      ? FingerPrintIcon
      : ShieldCheckIcon;

  const handleActivate = async () => {
    const success = await register();
    if (success) {
      onSuccess?.();
      onClose();
    }
  };

  const handleDismiss = () => {
    dismissPromotion();
    onClose();
  };

  const footer = (
    <div className="flex flex-row gap-3 w-full">
      <Button
        variant="outline"
        onClick={handleDismiss}
        className="flex-1"
        size="lg"
      >
        {t('passkey.notNow') || 'Ahora no'}
      </Button>
      <Button
        onClick={handleActivate}
        isLoading={isLoading}
        className="flex-1"
        size="lg"
      >
        {t('passkey.activate', { device: deviceLabel }) || `Activar ${deviceLabel}`}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      size="sm"
      footer={footer}
      showCloseButton={false}
      closeOnOutsideClick={false}
    >
      <div className="text-center py-4">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-6">
          <DeviceIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-3">
          {t('passkey.promotionTitle') || '¿Inicio más rápido?'}
        </h2>

        {/* Description */}
        <p className="text-secondary-600 dark:text-secondary-300 mb-2">
          {t('passkey.promotionDescription', { device: deviceLabel }) || 
            `Activa ${deviceLabel} para iniciar sesión con solo mirar tu dispositivo.`}
        </p>

        {/* Benefits */}
        <ul className="text-sm text-secondary-500 dark:text-secondary-400 space-y-1 mt-4">
          <li className="flex items-center justify-center gap-2">
            <ShieldCheckIcon className="w-4 h-4 text-green-500" />
            {t('passkey.benefit1') || 'Más seguro que contraseñas'}
          </li>
          <li className="flex items-center justify-center gap-2">
            <ShieldCheckIcon className="w-4 h-4 text-green-500" />
            {t('passkey.benefit2') || 'No tienes que recordar nada'}
          </li>
        </ul>

        {/* Error */}
        {error && (
          <p className="text-red-500 text-sm mt-4">{error}</p>
        )}
      </div>
    </Modal>
  );
}

